import { paths } from "~/router/paths";
import { IPlan, SubscriptionType } from "~/types/billing";
import { PlanStatus } from "~/types/account";
import { type OnboardingIconName } from "./OnboardingIcon";

// Smartli's onboarding: the user picks what Smartli should create, sees it
// done for an example product, and what the studio includes, before checkout.

// The main tools, picked on the first step. All three start picked.
export const ONBOARDING_CATEGORIES = [
  "ai_photoshoot",
  "product_descriptions",
  "ai_blogs",
] as const;

export type OnboardingCategory = (typeof ONBOARDING_CATEGORIES)[number];

// The other tools, shown as chips.
export const MORE_TOOLS = [
  "background_remover",
  "background_generator",
  "image_enhancer",
  "ads_writer",
] as const;

export type MoreTool = (typeof MORE_TOOLS)[number];

// Each tool's icon and the page its quick start opens.
export const CATEGORY_DETAILS: Record<
  OnboardingCategory,
  { icon: OnboardingIconName; path: string }
> = {
  ai_photoshoot: { icon: "user", path: paths["ai-models-photoshoot"].index },
  product_descriptions: { icon: "file", path: paths.description.index },
  ai_blogs: { icon: "news", path: paths.blog.index },
};

export const TOOL_ICONS: Record<MoreTool, OnboardingIconName> = {
  background_remover: "eraser",
  background_generator: "image",
  image_enhancer: "wand",
  ads_writer: "megaphone",
};

// Set when the user leaves for checkout and cleared once the welcome screen
// is dismissed. Holds the picked tools, so the welcome screen after payment
// can show their quick starts.
export const PICKED_CATEGORIES_KEY = "onboardingPickedCategories";

export const toggleCategory = (
  selected: OnboardingCategory[],
  category: OnboardingCategory
): OnboardingCategory[] =>
  selected.includes(category)
    ? selected.filter((item) => item !== category)
    : [...selected, category];

// The picked tools in the Figma order, or every tool when none was picked
// (Skip on the first step).
export const orderedCategories = (
  selected: readonly OnboardingCategory[]
): OnboardingCategory[] => {
  const picked = ONBOARDING_CATEGORIES.filter((category) =>
    selected.includes(category)
  );
  return picked.length > 0 ? picked : [...ONBOARDING_CATEGORIES];
};

export const readPickedCategories = (): OnboardingCategory[] | null => {
  try {
    const stored = localStorage.getItem(PICKED_CATEGORIES_KEY);
    if (stored === null) return null;
    const categories = JSON.parse(stored);
    return Array.isArray(categories)
      ? ONBOARDING_CATEGORIES.filter((category) =>
          categories.includes(category)
        )
      : [];
  } catch {
    return null;
  }
};

export type PlanDetails = {
  // The cheapest plan with AI Blogs and with AI Ads Writer, for the badges.
  blogsPlan: string;
  adsWriterPlan: string;
  // The cheapest plan's name and how many descriptions it includes.
  starterPlan: string;
  starterDescriptions: number;
};

// Read from the active monthly plans' limits. Until the plans load, or if
// they don't carry a limit, the design's values are shown.
export const getPlanDetails = (plans: IPlan[] | undefined): PlanDetails => {
  const monthly = (plans ?? [])
    .filter(
      (plan) =>
        plan.status === PlanStatus.ACTIVE &&
        plan.interval === SubscriptionType.MONTHLY
    )
    .sort((a, b) => a.cost_per_month - b.cost_per_month);
  const starter = monthly[0];

  return {
    blogsPlan:
      monthly.find((plan) => (plan.limits?.blog_generated ?? 0) > 0)?.name ??
      "Pro",
    adsWriterPlan:
      monthly.find((plan) => plan.limits?.ads_writer)?.name ?? "Empire",
    starterPlan: starter?.name ?? "Starter",
    starterDescriptions: starter?.limits?.descriptions_generated || 20,
  };
};
